import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createProjectsSlice } from "./projects/slice";
import { createUnitsSlice } from "./units/slice";
import { createEmployeesSlice } from "./employees/slice";
import { createEmployeePositionsSlice } from "./employeePositions/slice";
import { createSamplingSitesSlice } from "./samplingSites/slice";
import { createSamplingEventsListSlice } from "./samplingEventsList/slice";
import { createDelListsSlice } from "./delLists/slice";
import { createDelWorkflowSlice } from "./workflows/delWorkflow/slice";
import { createSafetyFormWorkflowSlice } from "./workflows/safetyFormWorkflow/slice";
import { createNotificationSlice } from "./notification/slice";
import { createNavigationSlice } from "./navigation/slice";
import { createLoadingSlice } from "./loading/slice";
import {
  createCreateSamplingEventUISlice,
  createCreateSamplingEventSlice,
} from "./createSamplingEvent/slice";
import { createSamplingEventsTabUISlice } from "./ui/landingPage/samplingEventsTab/slice";
import { createActiveDelSubmitsSlice } from "./activeDels/submits/slice";
import { createActiveDelDraftsSlice } from "./activeDels/drafts/slice";
import { createActiveDelApprovalsSlice } from "./activeDels/approvals/slice";
import { createDraftsTabUISlice } from "./ui/landingPage/draftsTab/slice";
import { createSubmitsTabUISlice } from "./ui/landingPage/submitsTab/slice";
import { createApprovalsTabUISlice } from "./ui/landingPage/approvalsTab/slice";
import { createDelReportingTabUISlice } from "./ui/landingPage/delReportingTab/slice";
import { createMainMenuSlice } from "./ui/landingPage/mainMenu/slice";
import { createLandingPageSlice } from "./ui/landingPage/landing/slice";
import { createReportingPageSlice } from "./ui/reportingPage/slice";
import { createAdminPageSlice } from "./ui/adminPage/slice";
import { idbJSONStorage, STORAGE_NAME } from "./persist/idbStorage";
import { initializeUserSession } from "../auth/auth";
import { restoreAllActiveDelPrototypes } from "./persist/prototypes";

import { draftsInitialState } from "./delLists/draftLists/delListsDraftsListsState";
import { submitsInitialState } from "./delLists/submitLists/delListsSubmitListsState";
import { approvalsInitialState } from "./delLists/approvalLists/delListsApprovalListsState";
import { approvedInitialState } from "./delLists/approvedLists/delListsApprovedListsState";
import { delReportsListsInitialState } from "./delLists/delReportsLists/delListsDelReportsListsState";
import { nearMissListInitialState } from "./safetyFormLists/nearMissList/safetyFormListsNearMissListsState";
import { safetyAuditListInitialState } from "./safetyFormLists/safetyAuditList/safetyFormListsSafetyAuditListState";
import { safetyFormReportsInitialState } from "./safetyFormLists/safetyFormReports/safetyFormListsSafetyFormReportsState";
import { createActiveDelReportingSlice } from "./activeDels/delReports/slice";
import { createActiveSamplingEventSlice } from "./activeSamplingEvent/slice";
import {
  restoreActiveSamplingEventPrototypes,
  restoreActiveSafetyAuditPrototypes,
  restoreActiveNearMissPrototypes,
} from "./persist/prototypes";
import { createCalibrationLogsListSlice } from "./calibrationLogsList/slice";
import { createActiveCalibrationLogBookSlice } from "./activeCalibrationLogBook/slice";
import { createCalibrationLogsTabUISlice } from "./ui/landingPage/calibrationLogsTab/slice";
import { createSafetyFormListsSlice } from "./safetyFormLists/slice";
import { createSafetyPageSlice } from "./ui/safetyPage/slice";
import { createActiveNearMissSlice } from "./activeSafetyForms/nearMiss/slice";
import { createActiveSafetyAuditFormSlice } from "./activeSafetyForms/safetyAuditForm/slice";

// Change this when we update states
const SCHEMA_VERSION = 7;

const stripForPersist = (state) => {
  const entries = Object.entries(state).filter(([k]) => !k.endsWith("Actions"));
  const persisted = Object.fromEntries(entries);

  // exclude always-fresh slices
  delete persisted.projects;
  delete persisted.employees;
  delete persisted.units;
  delete persisted.employeePositions;
  delete persisted.samplingSites;
  delete persisted.samplingSiteEvents;
  delete persisted.notification;
  delete persisted.navigation;
  delete persisted.loading;
  delete persisted.draftsTabUI;
  delete persisted.submitsTabUI;
  delete persisted.approvalsTabUI;
  delete persisted.delReportingTabUI;
  delete persisted.mainMenu;
  delete persisted.landingPage;
  delete persisted.reportingPage;
  delete persisted.safetyPage;
  // do not persist which subview is open in sampling events tab (route controls it)
  delete persisted.samplingEventsTabUI;
  // do not persist which subview is open in calibration logs tab (route controls it)
  delete persisted.calibrationLogsTabUI;
  // exclude sampling event form UI bucket (lives under activeSamplingEvent now)
  delete persisted.samplingEventForm; // legacy root key (safe no-op after refactor)
  // keep delLists UI meta but drop heavy items
  if (persisted.delLists) {
    const { drafts, submits, approvals, approved, delReportsLists, ...rest } =
      persisted.delLists;
    persisted.delLists = {
      ...rest,
      drafts: drafts ? { ...drafts, items: [] } : undefined,
      submits: submits
        ? { ...submits, items: [], selecetedSubmitIds: [] }
        : undefined,
      approvals: approvals ? { ...approvals, items: [] } : undefined,
      approved: approved ? { ...approved, items: [] } : undefined,
      delReportsLists: delReportsLists
        ? { ...delReportsLists, items: [] }
        : undefined,
    };
  }
  if (persisted.delReporting) {
    const { items, ...rest } = persisted.delReporting;
    persisted.delReporting = { ...rest, items: [] };
  }
  return persisted;
};

// shallow per-slice merge (default <- persisted)
const shallowMergeSlices = (defaults, persisted) => {
  if (!persisted || typeof persisted !== "object") return defaults;
  const merged = {};
  for (const k in defaults) {
    const defSlice = defaults[k];
    const pSlice = persisted[k];
    if (
      defSlice &&
      typeof defSlice === "object" &&
      !Array.isArray(defSlice) &&
      pSlice &&
      typeof pSlice === "object" &&
      !Array.isArray(pSlice)
    ) {
      merged[k] = { ...defSlice, ...pSlice };
    } else if (pSlice !== undefined) {
      merged[k] = pSlice;
    } else {
      merged[k] = defSlice;
    }
  }
  return merged;
};

// Factory (defaults)
export const useAppStore = create(
  persist(
    (set, get) => ({
      schemaVersion: SCHEMA_VERSION,
      sessionValidated: false,
      rehydrated: false,
      ...createProjectsSlice(set, get),
      ...createUnitsSlice(set, get),
      ...createEmployeesSlice(set, get),
      ...createEmployeePositionsSlice(set, get),
      ...createSamplingSitesSlice(set, get),
      ...createSamplingEventsListSlice(set, get),
      ...createDelListsSlice(set, get),
      ...createDelWorkflowSlice(set, get),
      ...createSafetyFormWorkflowSlice(set, get),
      ...createNotificationSlice(set, get),
      ...createNavigationSlice(set, get),
      ...createLoadingSlice(set, get),
      ...createCreateSamplingEventUISlice(set, get),
      ...createCreateSamplingEventSlice(set, get),
      ...createActiveDelSubmitsSlice(set, get),
      ...createActiveDelDraftsSlice(set, get),
      ...createActiveDelApprovalsSlice(set, get),
      ...createActiveDelReportingSlice(set, get),
      ...createActiveSamplingEventSlice(set, get),
      ...createCalibrationLogsListSlice(set, get),
      ...createActiveCalibrationLogBookSlice(set, get),
      ...createCalibrationLogsTabUISlice(set, get),
      ...createActiveNearMissSlice(set, get),
      ...createActiveSafetyAuditFormSlice(set, get),
      ...createDraftsTabUISlice(set, get),
      ...createSubmitsTabUISlice(set, get),
      ...createApprovalsTabUISlice(set, get),
      ...createDelReportingTabUISlice(set, get),
      ...createSamplingEventsTabUISlice(set, get),
      ...createMainMenuSlice(set, get),
      ...createLandingPageSlice(set, get),
      ...createReportingPageSlice(set, get),
      ...createAdminPageSlice(set, get),
      ...createSafetyPageSlice(set, get),
      ...createSafetyFormListsSlice(set, get),
    }),
    {
      name: STORAGE_NAME,
      storage: createJSONStorage(() => idbJSONStorage),
      partialize: (s) => stripForPersist(s),

      // merge persisted over current defaults slice-by-slice
      merge: (persisted, current) => {
        try {
          const parsed =
            typeof persisted === "string" ? JSON.parse(persisted) : persisted;
          const merged = shallowMergeSlices(current, parsed);

          // Deep-merge nested delLists to preserve new key
          if (merged.delLists && current.delLists) {
            const defDL = current.delLists;
            const perDL = merged.delLists;
            merged.delLists = {
              ...defDL,
              ...perDL,
              drafts: { ...defDL.drafts, ...perDL.drafts },
              submits: { ...defDL.submits, ...perDL.submits },
              approvals: { ...defDL.approvals, ...perDL.approvals },
              approved: { ...defDL.approved, ...perDL.approved },
              delReportsLists: {
                ...defDL.delReportsLists,
                ...perDL.delReportsLists,
              },
            };
          }

          // Deep-merge nested safetyFormLists to preserve new keys (e.g., selectedNearMissIds)
          if (merged.safetyFormLists && current.safetyFormLists) {
            const defSFL = current.safetyFormLists;
            const perSFL = merged.safetyFormLists;
            merged.safetyFormLists = {
              ...defSFL,
              ...perSFL,
              nearMissList: { ...defSFL.nearMissList, ...perSFL.nearMissList },
              safetyAuditList: { ...defSFL.safetyAuditList, ...perSFL.safetyAuditList },
              safetyFormReports: { ...defSFL.safetyFormReports, ...perSFL.safetyFormReports },
            };
          }

          return merged;
        } catch {
          return current;
        }
      },

      onRehydrateStorage: () => async (state) => {
        try {
          // Validate session
          await initializeUserSession();

          // Restore prototypes for DELs and Sampling Event after data is in memory
          const snapshot = useAppStore.getState();
          restoreAllActiveDelPrototypes(snapshot);
          restoreActiveSamplingEventPrototypes(snapshot);
          restoreActiveSafetyAuditPrototypes(snapshot);
          restoreActiveNearMissPrototypes(snapshot);
          useAppStore.setState(snapshot, false);

          // Always reset delLists to defaults on rehydrate to avoid stale filters/items
          useAppStore.setState(
            {
              delLists: {
                isLoading: false,
                error: null,
                drafts: draftsInitialState,
                submits: submitsInitialState,
                approvals: approvalsInitialState,
                approved: approvedInitialState,
                delReportsLists: delReportsListsInitialState,
              },
            },
            false
          );

          // Always reset safetyFormLists to defaults on rehydrate to avoid stale data
          useAppStore.setState(
            {
              safetyFormLists: {
                isLoading: false,
                error: null,
                nearMissList: nearMissListInitialState,
                safetyAuditList: safetyAuditListInitialState,
                safetyFormReports: safetyFormReportsInitialState,
              },
            },
            false
          );

          // Versioned migrations
          useAppStore.setState((s) => {
            const v = s.schemaVersion ?? 1;
            const next = { ...s };

            if (v < SCHEMA_VERSION) {
              next.schemaVersion = SCHEMA_VERSION;
            }

            return next;
          }, false);

          // Mark flags
          useAppStore.setState(
            { sessionValidated: true, rehydrated: true },
            false
          );
        } catch (err) {
          // session invalid → clear IndexedDB and proceed with fresh defaults
          await idbJSONStorage.removeItem(STORAGE_NAME);
          useAppStore.setState(
            { sessionValidated: true, rehydrated: true },
            false
          );
        }
      },
    }
  )
);
